import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { IconButtonProps } from '@nexds/web'

import { InjectionTokens } from '@/controller/tokens'

import {
  ScreenElementsState,
  ScreenElementsStateMutator,
  ScreenElementsStateProps
} from '../../../MapScreen/states/ScreenElementsState'
import { NewsResponse } from '../../../NewsScreen/types'
import { Drawer } from '../../components/Drawer'
import { canDrawerContentExpand } from '../../components/Drawer/Drawer.constants'
import { WrittenSearchPresenter } from '../WrittenSearchPresenter'
import { DrawerImperativeSubject } from './DrawerImperativeInterface'

import { NEXATLAS_API_BASE } from '@/infra/http/apiBaseUrls'
import { IHttpClient } from '@/infra/http/IHttpClient'
import { LocalStorageKeyValuePersister } from '@/infra/keyValuePersister/LocalStorageKeyValuePersister'
import { useInjection } from '@/presentation/contexts/InjectionContext'
import { useBehaviorSubject } from '@/presentation/hooks/useBehaviorSubject'
import { useFeatureFlags } from '@/presentation/hooks/useFeatureFlags'
import { useI18n } from '@/presentation/hooks/useI18n'
import { usePageEnteredMetrics } from '@/presentation/hooks/usePageEnteredMetrics'
import { closeDrawer, openDrawer } from '@/presentation/router/DrawerRouter'
import { RouterPaths } from '@/presentation/router/RouterPathsMapper'
import { isTesting } from '@/utils/testing'

export interface DrawerItemProps {
  routePath: RouterPaths
  title: string
  icon: IconButtonProps['icon']
  haveContent: boolean
  isInBeta?: boolean
  hasNotification?: boolean
}

function DrawerPresenter() {
  const routerLocation = useLocation()
  const navigate = useNavigate()
  const { t } = useI18n()
  const featureFlags = useFeatureFlags()
  const httpClient = useInjection<IHttpClient>(InjectionTokens.HttpClient)
  const persister = useInjection<LocalStorageKeyValuePersister>(InjectionTokens.KeyValuePersister)

  const [hasNewsNotification, setHasNewsNotification] = useState(false)

  // Único ponto de observação de rota do app: emite PLANNINGAPP_PAGE_ENTERED por tela.
  usePageEnteredMetrics()

  useEffect(() => {
    const checkNews = async () => {
      const storedId = await persister.get('last_seen_news_id')
      const seenId = storedId.isSuccess && storedId.getValue() ? parseInt(storedId.getValue()!, 10) : 0

      const data = await httpClient.get<NewsResponse>(`${NEXATLAS_API_BASE}/news/get?page=0`)
      if (data.success && data.response.news.length > 0) {
        const latestId = data.response.news[0].id
        if (latestId > seenId) {
          setHasNewsNotification(true)
        }
      }
    }

    checkNews()
  }, [])

  const topItems = useMemo(
    () =>
      [
        {
          routePath: RouterPaths.MAP,
          icon: 'Map',
          title: t('DRAWER_ITEM_MAP_LABEL'),
          haveContent: false
        },
        {
          routePath: RouterPaths.AIRCRAFT,
          icon: 'Aircraft',
          title: t('DRAWER_ITEM_AIRCRAFT_LABEL'),
          haveContent: true
        },
        {
          routePath: RouterPaths.ROUTES_PANEL,
          icon: 'Route',
          title: t('DRAWER_ITEM_ROUTE-PANEL_LABEL'),
          haveContent: true
        },
        {
          routePath: RouterPaths.WEIGHT_AND_BALANCE,
          icon: 'Wb',
          title: t('DRAWER_ITEM_WEIGHT-AND-BALANCE_LABEL'),
          haveContent: true
        },
        ...(featureFlags.checklists
          ? [
              {
                routePath: RouterPaths.CHECKLISTS,
                icon: 'Checklist',
                title: t('CHECKLISTS_DRAWER-ITEM_LABEL'),
                haveContent: true,
                isInBeta: true
              }
            ]
          : []),
        {
          routePath: RouterPaths.VERTICAL_NAVIGATION,
          icon: 'Vertical',
          title: t('DRAWER_ITEM_VERTICAL-NAVIGATION_LABEL'),
          haveContent: true
        },
        {
          routePath: RouterPaths.SAVED_ROUTES,
          icon: 'Star',
          title: 'Rotas salvas',
          haveContent: true
        },
        {
          routePath: RouterPaths.ADHP_INFO,
          icon: 'Aerodrome',
          title: t('DRAWER_ITEM_ADHP-INFO_LABEL'),
          haveContent: true
        },
        {
          routePath: RouterPaths.PROCEDURE_CHARTS,
          icon: 'Charts',
          title: t('DRAWER_ITEM_PROCEDURE_CHARTS'),
          haveContent: true
        },
        {
          routePath: RouterPaths.MY_CONTENT,
          icon: 'Folder',
          title: t('DRAWER_ITEM_MY-CONTENT_LABEL'),
          haveContent: true,
          isInBeta: true
        },
        {
          routePath: RouterPaths.MARKET,
          icon: 'Market',
          title: t('DRAWER_ITEM_MARKET_LABEL'),
          haveContent: true
        }
      ] as DrawerItemProps[],
    [featureFlags.checklists, t]
  )

  const bottomItems = useMemo(
    () =>
      [
        {
          routePath: RouterPaths.MOBILE,
          icon: 'Mobile',
          title: t('DRAWER_ITEM_MOBILE_LABEL'),
          haveContent: true
        },
        {
          routePath: RouterPaths.NEWS,
          icon: 'News',
          title: t('DRAWER_ITEM_NEWS_LABEL'),
          haveContent: true,
          hasNotification: hasNewsNotification
        },
        {
          routePath: RouterPaths.SETTINGS,
          icon: 'Settings',
          title: t('DRAWER_ITEM_SETTINGS_LABEL'),
          haveContent: true
        }
      ] as DrawerItemProps[],
    [t, hasNewsNotification]
  )

  if (isTesting()) {
    bottomItems.unshift({
      routePath: RouterPaths.BETA,
      icon: 'Test',
      title: t('DRAWER_ITEM_BETA_LABEL'),
      haveContent: true
    })
  }

  const defaultOption = RouterPaths.MAP

  const [isMainDrawerOpen, setIsMainDrawerOpen] = useState(false)
  const [activeOption, setActiveOption] = useState<RouterPaths>(defaultOption)
  // A largura vive no estado global: telas que expandem o Drawer (ver `useExpandedDrawer`) podem
  // montar antes deste presenter assinar o `DrawerImperativeSubject`, que não guarda o último evento.
  const screenElementsState = useBehaviorSubject<ScreenElementsStateProps>(ScreenElementsState)
  const contentWidthVariant = screenElementsState.drawerContentWidthVariant
  const additionalBetaRoutes: (string | RegExp)[] = [] // Adicione aqui as rotas (string ou regex) que devem ter a borda amarela de beta
  useEffect(() => {
    const eventSubscription = DrawerImperativeSubject.subscribe((event) => {
      switch (event.type) {
        case 'close':
          closeDrawer()
          break
        case 'open':
          openDrawer(event.screen, event.stateLocation)
          break
        case 'set-content-width':
          ScreenElementsStateMutator.setDrawerContentWidthVariant(event.variant ?? 'default')
          break
        default:
          break
      }
    })

    return () => {
      eventSubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (routerLocation.pathname === '/') {
      navigate(defaultOption)
    }
    if (routerLocation.pathname === RouterPaths.MAP || routerLocation.pathname === '/') {
      ScreenElementsStateMutator.setDrawer(false)
    } else {
      ScreenElementsStateMutator.setDrawer(true)
    }
    setActiveOption(routerLocation.pathname as RouterPaths)
  }, [defaultOption, navigate, routerLocation])

  useEffect(() => {
    if (!canDrawerContentExpand(routerLocation.pathname) && contentWidthVariant !== 'default') {
      ScreenElementsStateMutator.setDrawerContentWidthVariant('default')
    }
  }, [contentWidthVariant, routerLocation.pathname])

  useEffect(() => {
    ScreenElementsStateMutator.setMainDrawer(isMainDrawerOpen)
  }, [isMainDrawerOpen])

  useEffect(() => {
    if (activeOption === RouterPaths.NEWS) {
      setHasNewsNotification(false)
    }
  }, [activeOption])

  return (
    <>
      <Drawer
        topItems={topItems}
        bottomItems={bottomItems}
        defaultOption={defaultOption}
        activeOption={activeOption}
        setActiveOption={setActiveOption}
        isMainDrawerOpen={isMainDrawerOpen}
        setIsMainDrawerOpen={setIsMainDrawerOpen}
        additionalBetaRoutes={additionalBetaRoutes}
        contentWidthVariant={contentWidthVariant}
      />
      <WrittenSearchPresenter shouldOverlap={activeOption === RouterPaths.ROUTES_PANEL && !isMainDrawerOpen} />
    </>
  )
}

export { DrawerPresenter }
